import { useEffect } from "react";
import { ErrorBoundary } from "~/components/error-boundary";
import { useAppNavDrawer } from "~/providers/app-nav-drawer";
import {
  OpenInNew as OpenInNewIcon,
  People as UsersIcon,
} from "@mui/icons-material";
import { useViewer } from "~/providers/viewer";
import { Navigate } from "react-router-dom";
import { useCanUpdateOrganization } from "~/lib/iam/actions/mondoo/captain/Captain/UpdateOrganization";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import {
  BusinessIntelligenceIcon,
  HomeIcon,
  SettingsIcon,
  SpacesIcon,
  SecurityPipelineNavIcon,
  PolicyIcon,
} from "~/icons";
import { useCanGetCachedOverview } from "~/lib/iam/actions/mondoo/orgoverview/OrgOverviewService/GetCachedOverview";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { useCanListOrganizationMemberships } from "~/lib/iam/actions/mondoo/captain/Captain/ListOrganizationMemberships";
import { OrganizationRoutes } from "~/routes";

export function OrganizationPages() {
  const { isFeatureEnabled } = useFeatureFlags();
  const { setTree } = useAppNavDrawer();
  const { currentOrg } = useViewer();
  const org = currentOrg();
  const canUpdateOrganization = useCanUpdateOrganization();
  const canViewOrgOverview = useCanGetCachedOverview();
  const canCreateIntegration = useCanCreate();
  const canListMembers = useCanListOrganizationMemberships();
  const isPolicyManagementEnabled = isFeatureEnabled(
    "Organization Policy Management",
  );
  const isUserManagementEnabled = isFeatureEnabled("User Management");
  const isSecurityPipelineEnabled = isFeatureEnabled("Security Pipeline");
  const isMondooBiDemoEnabled = isFeatureEnabled("Mondoo BI Demo");
  useEffect(() => {
    setTree(
      [
        canViewOrgOverview
          ? {
              text: "Overview",
              icon: <HomeIcon />,
              link: {
                to: `/organization/overview?organizationId=${org?.id}`,
              },
            }
          : null,
        {
          text: "Spaces",
          icon: <SpacesIcon />,
          link: {
            to: `/organization/spaces?organizationId=${org?.id}`,
          },
        },
        isPolicyManagementEnabled && org
          ? {
              text: "Policies",
              icon: <PolicyIcon />,
              link: {
                to: `/organization/policies?organizationId=${org?.id}`,
              },
            }
          : null,
        isUserManagementEnabled && canListMembers
          ? {
              text: "Users",
              icon: <UsersIcon />,
              link: {
                to: `/organization/users?organizationId=${org?.id}`,
              },
            }
          : null,
        isSecurityPipelineEnabled && canCreateIntegration
          ? {
              text: "Security Pipeline",
              icon: <SecurityPipelineNavIcon />,
              link: {
                to: `/organization/integrations/security_pipeline_github?organizationId=${org?.id}`,
              },
            }
          : null,
        isMondooBiDemoEnabled && org?.id === "lunalectric"
          ? {
              text: "Mondoo BI",
              icon: <BusinessIntelligenceIcon />,
              endIcon: <OpenInNewIcon />,
              link: {
                to: `https://mondoo-bi-7ef3bb7d8c57f2da-xvtvwhy6uq-ue.a.run.app/`,
              },
              openInNew: true,
            }
          : null,
        canUpdateOrganization
          ? {
              text: "Settings",
              icon: <SettingsIcon />,
              link: {
                to: `/organization/settings?organizationId=${org?.id}`,
              },
            }
          : null,
      ].flatMap((navItem) => navItem ?? []),
    );
    return () => setTree([]);
  }, [
    org,
    canUpdateOrganization,
    canListMembers,
    canViewOrgOverview,
    canCreateIntegration,
    isPolicyManagementEnabled,
    isUserManagementEnabled,
    isSecurityPipelineEnabled,
    isMondooBiDemoEnabled,
  ]);

  if (!org) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ErrorBoundary key="organization">
      <OrganizationRoutes {...{ org }} />
    </ErrorBoundary>
  );
}
